import { uniq, extend, mapValues, difference, flow, reduce, values, flatten } from 'lodash';
import {
  adRevAndRevDataKpis,
  adRevenueKpis,
  allKpis,
  allRevenueKpis,
  clickAttributionTypeKpis,
  clickInjectionKpis,
  conversionRateKpis,
  deattributionKpis,
  impressionAttributionTypeKpis,
  revenueKpis,
  uninstallKpis,
} from '@lib/utils/kpi';

function Kpi(gettext, Account, KpiCohorts, User, $q) {
  'ngInject';

  return {
    getListByScreen: getListByScreen,
    getAll: getAll,
    splitEvent: splitEvent,
  };

  /**
   * Get list of kpis for particular predefined screen
   *
   * @param {String} screen - current screen
   * @param {String} attributionType - current attribution type
   * @param {String} attributionSource - current attribution source
   * @param {Boolean} includeProtectedKpis
   * @param {String} grouping - current view-by selection
   * @param {String} trackerToken
   * @returns {Promise} list of kpis
   */
  function getListByScreen(options = {}) {
    const { screen, attributionType, attributionSource, includeProtectedKpis = true, grouping, trackerToken } = options;

    return _getList({
      attributionType,
      attributionSource,
      includeProtectedKpis,
      grouping,
      trackerToken,
    }).then(result => result[screen]);
  }

  /**
   * Get all available columns depending on enabled features and packages
   *
   * @param {String} attributionType - current attribution type
   * @param {String} attributionSource - current attribution source
   * @param {String=overview} type - type of the table, overview or cohorts
   * @param {String} grouping - current view-by selection
   * @param {String} trackerToken
   * @returns {Promise} list of kpis
   */
  function getAll(options = {}) {
    const { attributionType, attributionSource, type = 'overview', grouping, trackerToken } = options;

    return $q
      .all([
        User.getCurrent(),
        Account.getCurrent(),
        _getList({
          attributionType,
          attributionSource,
          grouping,
          trackerToken,
        }),
      ])
      .then(function(result) {
        var [item, fraudPackage, list] = result;
        var kpis = list[type];

        if (type !== 'cohorts') {
          if (item.features.cost_data.enabled) {
            kpis = uniq(kpis.concat(list.cost));
          }
          if (Account.hasFraudPackage(fraudPackage)) {
            kpis = uniq(kpis.concat(list['fraud-prevention']));
          }
        }

        return kpis;
      });
  }

  /**
   * Get list of kpis separated by predefined screens
   *
   * @param {String} attributionType - current attribution type
   * @param {String} attributionSource - current attribution source
   * @param {Boolean} includeProtectedKpis
   * @param {String} grouping - current view-by selection
   * @param {String} trackerToken
   * @returns {Promise} nested list of available kpis
   * @private
   */
  function _getList(options = {}) {
    const { attributionType, attributionSource, includeProtectedKpis = true, grouping, trackerToken } = options;

    return $q
      .when(allKpis)
      .then(kpis => _filterDeattributionKpi(kpis, attributionSource))
      .then(kpis => _checkAttributionType(kpis, attributionType))
      .then(kpis => _filterConversionRateKpi(kpis, trackerToken, grouping))
      .then(kpis => _filterStatisticsForAllAppsKpis(kpis, includeProtectedKpis))
      .then(_checkRevenueAsFeature)
      .then(_checkClickInjectionFeature)
      .then(_checkUninstallFeature)
      .then(_checkAdRevenuePackage)
      .then(_checkAdRevenueAndRevenueDataPermissions)
      .then(kpis => _appendCohortKpis(kpis, attributionSource));
  }

  /**
   * Append cohort kpis
   *
   * @param {Object} kpis - list of kpis
   * @param {String} attributionSource - current attribution source
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _appendCohortKpis(kpis, attributionSource) {
    return KpiCohorts.getAllAvailable(attributionSource).then(function(cohorts) {
      return extend({}, kpis, { cohorts: cohorts });
    });
  }

  /**
   * Conditionally remove revenue kpis if the user does not have access
   *
   * @param {Object} kpis - list of kpis
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _checkRevenueAsFeature(kpis) {
    return $q.all([Account.getCurrent(), User.getCurrent()]).then(([account, user]) => {
      const hasPermissions = Account.hasRevenueDataFeature(account) && User.featureIsEnabled(user, 'revenue_data');

      return hasPermissions ? kpis : _removeKpis(kpis, revenueKpis);
    });
  }

  /**
   * Conditionally remove Ad Revenue kpis if the user does not have access
   *
   * @param {Object} kpis - list of kpis
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _checkAdRevenuePackage(kpis) {
    return $q.all([User.getCurrent(), Account.getCurrent()]).then(([user, account]) => {
      const condition = User.featureIsEnabled(user, 'ad_revenue') && Account.hasAdRevenuePackage(account);

      return !condition ? _removeKpis(kpis, adRevenueKpis) : kpis;
    });
  }

  /**
   * Removes `Ad Revenue` and `Revenue Data` KPIs if an account does not have `Ad Revenue` package or `Revenue Data` feature enabled
   * or a user does not have permissions for Revenue data (`ad_revenue`) or In-app Revenue data (`revenue_data`).
   *
   * @param {Object} kpis list of KPIs
   * @returns {Object} list of filtered KPIs
   * @private
   */
  function _checkAdRevenueAndRevenueDataPermissions(kpis) {
    return $q.all([Account.getCurrent(), User.getCurrent()]).then(([account, user]) => {
      const hasPermissions =
        Account.hasAdRevenuePackage(account) &&
        Account.hasRevenueDataFeature(account) &&
        User.featureIsEnabled(user, 'ad_revenue') &&
        User.featureIsEnabled(user, 'revenue_data');

      return hasPermissions ? kpis : _removeKpis(kpis, adRevAndRevDataKpis);
    });
  }

  /**
   * Conditionally remove click injection kpis
   * if the feature is not enabled for account
   *
   * @param {Object} kpis - list of kpis
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _checkClickInjectionFeature(kpis) {
    return Account.getCurrent().then(function(account) {
      return !Account.hasFeature(account, 'click_injection_filtering') ? _removeKpis(kpis, clickInjectionKpis) : kpis;
    });
  }

  /**
   * Conditionally remove uninstall reinstall kpis
   * if the feature is not enabled for account
   *
   * @param {Object} kpis - list of kpis
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _checkUninstallFeature(kpis) {
    return Account.getCurrent().then(function(account) {
      return !Account.hasUninstallPackage(account) ? _removeKpis(kpis, uninstallKpis) : kpis;
    });
  }

  /**
   * Remove `clicks` from deliverables if the attribution type is impression
   *
   * @param {Object} kpis - list of kpis
   * @param {String} attributionType - attribution type
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _checkAttributionType(kpis, attributionType) {
    return attributionType === 'impression'
      ? _removeKpis(kpis, clickAttributionTypeKpis)
      : _removeKpis(kpis, impressionAttributionTypeKpis);
  }

  /**
   * Remove `deattributions` kpi from deliverables if the attribution source is dynamic
   *
   * @param {Object} kpis - list of kpis
   * @param {String} attributionSource - current attribution source
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _filterDeattributionKpi(kpis, attributionSource) {
    return attributionSource === 'dynamic' ? _removeKpis(kpis, deattributionKpis) : kpis;
  }

  function _filterConversionRateKpi(kpis, trackerToken = null, grouping = 'trackers') {
    return !trackerToken && grouping !== 'trackers' ? _removeKpis(kpis, conversionRateKpis) : kpis;
  }

  /**
   * Removes `Ad Revenue` and `All Revenue` KPIs for the Statistics for All Apps page
   *
   * @param {Object} kpis list of KPIs
   * @param {Boolean} includeProtectedKpis
   * @returns {Object} list of filtered KPIs
   * @private
   */
  function _filterStatisticsForAllAppsKpis(kpis, includeProtectedKpis) {
    return User.getCurrent().then(user =>
      !includeProtectedKpis && user.multiple_accounts_view
        ? _removeKpis(kpis, [...adRevenueKpis, ...allRevenueKpis])
        : kpis,
    );
  }

  /**
   * Remove the specified kpis from the kpi list
   *
   * @param {Object} kpis - list of kpis
   * @param {Array} kpisToRemove - list of kpis to remove
   * @returns {Object} list of filtered kpis
   * @private
   */
  function _removeKpis(kpis, kpisToRemove) {
    return mapValues(kpis, function(values) {
      return difference(values, kpisToRemove);
    });
  }

  /**
   * Flat list of available kpis
   *
   * @returns {Array} list of available kpis
   * @private
   */
  function _flattenAvailableKpis() {
    return flow(values, flatten, uniq)(allKpis);
  }

  /**
   * Split custom events from the kpi list
   *
   * @param {Array} columns - currently set columns
   * @param {Array=} kpisForCurrentScreen - kpi list for current screen
   * @returns {Promise} list of kpis and custom events
   */
  function splitEvent(columns, kpisForCurrentScreen) {
    var kpis = _flattenAvailableKpis();

    if (kpisForCurrentScreen) {
      columns = uniq(columns.concat(kpisForCurrentScreen));
    }

    var list = reduce(columns, function(final, column) {
      if (kpis.includes(column)) {
        final.kpis.push(column);
      } else {
        final.events.push(column);
      }
      return final;
    }, { kpis: [], events: [] });

    return $q.when(list);
  }
}

export { Kpi };
